import { type RefObject, useCallback, useEffect, useState } from 'react';
import { type ViewInstance } from 'react-native';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Tracks whether keyboard focus is on the element or any of its descendants.
 * Mouse focus is ignored, so clicking a toast keeps the hover-only behavior.
 */
export const useFocusedWithin = (ref: RefObject<ViewInstance | HTMLElement | null>) => {
  //
  // ─── State ───────────────────────────────────────────────────────────

  const [isFocusedWithin, setIsFocusedWithin] = useState(false);

  // ─── Handlers ────────────────────────────────────────────────────────

  const trackFocus = useCallback(() => {
    const node = ref.current as HTMLElement | null;

    if (!node) {
      return;
    }

    function handleFocusIn(event: FocusEvent) {
      const target = event.target as Element;

      setIsFocusedWithin(target.matches(':focus-visible'));
    }

    function handleFocusOut(event: FocusEvent) {
      const isFocusMovingInside = node?.contains(event.relatedTarget as Node | null) ?? false;

      if (isFocusMovingInside) {
        return;
      }

      setIsFocusedWithin(false);
    }

    // Firefox and Safari fire no focusout when the focused element is removed.
    function handleChildrenChange() {
      const isFocusInside = node?.contains(document.activeElement) ?? false;

      if (isFocusInside) {
        return;
      }

      setIsFocusedWithin(false);
    }

    const childrenObserver = new MutationObserver(handleChildrenChange);

    node.addEventListener('focusin', handleFocusIn);
    node.addEventListener('focusout', handleFocusOut);
    childrenObserver.observe(node, { childList: true, subtree: true });

    return function stopTrackingFocus() {
      node.removeEventListener('focusin', handleFocusIn);
      node.removeEventListener('focusout', handleFocusOut);
      childrenObserver.disconnect();
    };
  }, [ref]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(trackFocus, [trackFocus]);

  return isFocusedWithin;
};
